import React from 'react';
import {
  Award,
  Eye,
  Loader2,
  Users,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { useAdminContext } from '../../../contexts/AdminContext';

const BetaModals = () => {
  const {
    codeLoading,
    handleCreateCode,
    handleCreateTester,
    newCodeData,
    newTesterData,
    selectedFeedback,
    setNewCodeData,
    setNewTesterData,
    setSelectedFeedback,
    setShowCreateCodeModal,
    setShowCreateTesterModal,
    showCreateCodeModal,
    showCreateTesterModal,
    testerLoading,
    toUpperCase,
  } = useAdminContext();

  return (
    <>
    {/* Screenshot Preview Dialog */}
    <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-purple-400" />
            Screenshot: {selectedFeedback?.title}
          </DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-500 dark:text-slate-400">
            Ingediend door {selectedFeedback?.tester_name} op pagina {selectedFeedback?.page_url}
          </DialogDescription>
        </DialogHeader>
        {selectedFeedback?.screenshot && (
          <div className="mt-4">
            <img
              src={selectedFeedback.screenshot}
              alt="Screenshot"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-600"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Create Invite Code Modal */}
    <Dialog open={showCreateCodeModal} onOpenChange={setShowCreateCodeModal}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-yellow-400" />
            Nieuwe Uitnodigingscode
          </DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-500 dark:text-slate-400">
            Genereer een code waarmee beta-testers zich kunnen registreren
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Code (optioneel - wordt automatisch gegenereerd)
            </label>
            <Input
              value={newCodeData.code}
              onChange={(e) => setNewCodeData({ ...newCodeData, code: e.target.value.toUpperCase() })}
              placeholder="bijv. BETA2025"
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white uppercase"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Max. aantal keer te gebruiken
            </label>
            <Input
              type="number"
              min="1"
              value={newCodeData.max_uses}
              onChange={(e) => setNewCodeData({ ...newCodeData, max_uses: e.target.value })}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Vervaldatum (optioneel)
            </label>
            <Input
              type="date"
              value={newCodeData.expires_at}
              onChange={(e) => setNewCodeData({ ...newCodeData, expires_at: e.target.value })}
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowCreateCodeModal(false)}
              className="flex-1 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
            >
              Annuleren
            </Button>
            <Button
              onClick={handleCreateCode}
              disabled={codeLoading}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {codeLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Bezig...
                </>
              ) : (
                'Genereren'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Create Beta Tester Modal */}
    <Dialog open={showCreateTesterModal} onOpenChange={setShowCreateTesterModal}>
      <DialogContent className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-green-400" />
            Beta-tester Toevoegen
          </DialogTitle>
          <DialogDescription className="text-slate-500 dark:text-slate-500 dark:text-slate-400">
            Maak direct een beta-tester account aan (zonder uitnodigingscode)
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Naam <span className="text-red-400">*</span>
            </label>
            <Input
              value={newTesterData.name}
              onChange={(e) => setNewTesterData({ ...newTesterData, name: e.target.value })}
              placeholder="Volledige naam"
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              E-mailadres <span className="text-red-400">*</span>
            </label>
            <Input
              type="email"
              value={newTesterData.email}
              onChange={(e) => setNewTesterData({ ...newTesterData, email: e.target.value })}
              placeholder="email@voorbeeld.com"
              className="bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowCreateTesterModal(false)}
              className="flex-1 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
            >
              Annuleren
            </Button>
            <Button
              onClick={handleCreateTester}
              disabled={testerLoading || !newTesterData.name || !newTesterData.email}
              className="flex-1 bg-green-500 hover:bg-green-600 text-slate-900 dark:text-white"
            >
              {testerLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Bezig...
                </>
              ) : (
                'Toevoegen'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default BetaModals;
